import { ChangeDetectionStrategy, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { HeaderService } from '@app/shell/header/service/header/header.service';
import { UserService } from '@app/user';
import { BehaviorSubject, Subscription, catchError, debounceTime, filter, map, of, switchMap, tap } from 'rxjs';
import { Container, setGrow } from '../../animations/grow.animation';
import { RedirectDirective } from '../../directives/redirect.directive';
import { SearchResults } from '../../models/search-results';
import { HttpService } from '../../services/http.service';
import { SearchExploreService } from '../../services/search-explore.service';

@Component({
    selector: 'app-results',
    templateUrl: './results.component.html',
    styleUrls: ['./results.component.scss'],
    animations: [setGrow('0.4s', '0.4s'), Container],
    providers: [RedirectDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ResultsComponent implements OnChanges, OnInit, OnDestroy {
    @HostListener('keydown.tab', ['$event']) tab = this.preventDefault;
    @HostListener('keydown.shift.tab', ['$event']) shiftTab = this.preventDefault;

    @Input() value: string;

    private readonly httpService = inject(HttpService);
    private readonly userService = inject(UserService);
    private readonly headerService = inject(HeaderService);
    private readonly searchExploreService = inject(SearchExploreService);

    results: SearchResults;
    readonly isExplore$ = this.searchExploreService.isExplore$;
    readonly loading$ = new BehaviorSubject<boolean>(false);
    readonly error$ = new BehaviorSubject<boolean>(false);
    readonly skillCategoryLoaded$ = this.headerService.skillCategoryLoaded$;
    readonly hasTrial$ = this.userService.user$.pipe(map((user) => user.company.trial));
    private readonly _term$ = new BehaviorSubject<string>('');
    private readonly results$ = this._term$.pipe(
        map((term) => term.trim()),
        filter((term) => term !== ''),
        tap(() => {
            this.loading$.next(true);
            this.results = undefined;
        }),
        debounceTime(500),
        switchMap((term) => {
            this.loading$.next(true);
            this.error$.next(false);
            this.results = undefined;
            return this.httpService.search(term).pipe(
                tap((results) => {
                    this.results = results;
                    this.loading$.next(false);
                }),
                catchError(() => of(this.error$.next(true))),
            );
        }),
    );

    readonly listId = this.searchExploreService.resultsListId;

    private readonly subscriptions$ = new Subscription();

    private preventDefault(event: Event) {
        event.preventDefault();
    }

    get hasExtendedExplore() {
        return this.userService.access.includes('explore-extended');
    }

    get hasContent() {
        return (this.results?.contents?.length ?? 0) > 0;
    }

    get hasRelatedTerms() {
        return (this.results?.relatedTerms?.length ?? 0) > 0;
    }

    get availableSkillCategories() {
        return this.headerService.availableSkillCategories;
    }

    get hasCustomLogo() {
        return this.userService.hasCoBrandingLogo;
    }

    ngOnInit(): void {
        this.subscriptions$.add(this.results$.subscribe());
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] !== undefined && (!this.searchExploreService.isExplore || this._term$.value !== changes['value'].currentValue)) {
            this._term$.next(changes['value'].currentValue);
        }
    }
}
